import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  BookOpen,
  Star,
  PoundSterling,
  ArrowRight,
  HardHat,
  Users,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/quoting-software-for-contractors";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quoting Software for Contractors",
  "description": "Quoting software for contractors. Generate accurate, professional construction quotes from drawings, photos or a job description in under 10 minutes.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Quoting Software for Contractors", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best quoting software for contractors?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential contractors, The Proper Tool is purpose-built for the job: a 31-category UK cost library, AI quote generation from drawings or descriptions, expert scope validation, and branded PDF output. It starts at £29/month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "How does quoting software for contractors work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You upload drawings, attach site photos, or describe the job in plain language. The software reads your inputs, generates a fully itemised scope with quantities and UK market rates, and validates the quote for scope gaps. You review each line, make adjustments, and export a branded PDF to send to your client.",
      },
    },
    {
      "@type": "Question",
      "name": "Can contractors use The Proper Tool for multi-trade jobs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool covers 31 trade categories in a single quote: groundworks, steelwork, carpentry, plastering, M&E, roofing, drainage, landscaping, and more. A contractor running multi-trade residential projects gets a fully itemised breakdown across all trades in one document.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there free quoting software for contractors?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month. Fully free tools exist but typically lack UK-specific pricing, multi-trade categories, or professional PDF export.",
      },
    },
    {
      "@type": "Question",
      "name": "Does contractor quoting software work for smaller firms?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is designed for contractors of all sizes, from sole traders to small and mid-size firms. The Lite plan (£29/month) gives 10 AI quotes per month with full cost library access. Larger contractors can step up to Starter (25 quotes) or Pro (50 quotes) as volume grows.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Zap,
    title: "AI quote generation",
    description: "Describe the job or upload drawings and the AI builds a fully itemised scope with UK market rates. Typically done in under 10 minutes.",
  },
  {
    icon: FileText,
    title: "Reads structural drawings",
    description: "Upload architect's drawings or structural plans as PDFs. The tool extracts beam sizes, spans, dimensions, and floor areas to improve quote accuracy.",
  },
  {
    icon: BookOpen,
    title: "31 trade categories",
    description: "From groundworks and steelwork to M&E, roofing, and landscaping: every common residential trade is covered in a single quote.",
  },
  {
    icon: Star,
    title: "Expert scope validation",
    description: "Built-in Master Builder and Chartered Engineer review flags pricing outliers, scope gaps, and missing dependencies before you send.",
  },
  {
    icon: PoundSterling,
    title: "UK pricing database",
    description: "462 rated tasks across 31 categories with current UK market rates. Low, Typical, and High bands built in. Override any rate with your own figures.",
  },
  {
    icon: HardHat,
    title: "Branded PDF output",
    description: "Every quote exports as a professionally formatted PDF with your company details, full line-item scope, pricing assumptions, and trade section totals.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job or upload drawings",
    description: "Type a plain-text description of the project, upload architect's drawings or structural plans, attach site photos, or any combination. The Proper Tool works from whatever you have.",
  },
  {
    step: "2",
    title: "AI generates the itemised scope",
    description: "The AI reads your inputs and produces a fully itemised scope: every trade, every line item, quantities and unit rates drawn from the UK pricing database across all 31 categories.",
  },
  {
    step: "3",
    title: "Review, adjust, and send",
    description: "Review every line, apply or dismiss validation flags, add or remove items, then export a branded PDF to send directly to your client.",
  },
];

const FAQS = [
  {
    q: "What is the best quoting software for contractors?",
    a: "For UK residential contractors, The Proper Tool is purpose-built for the job: a 31-category UK cost library, AI quote generation from drawings or descriptions, expert scope validation, and branded PDF output. It starts at £29/month with a 14-day free trial and no credit card required.",
  },
  {
    q: "How does quoting software for contractors work?",
    a: "You upload drawings, attach site photos, or describe the job in plain language. The software reads your inputs, generates a fully itemised scope with quantities and UK market rates, and validates the quote for scope gaps. You review each line, make adjustments, and export a branded PDF to send to your client.",
  },
  {
    q: "Can contractors use The Proper Tool for multi-trade jobs?",
    a: "Yes. The Proper Tool covers 31 trade categories in a single quote: groundworks, steelwork, carpentry, plastering, M&E, roofing, drainage, landscaping, and more. A contractor running multi-trade residential projects gets a fully itemised breakdown across all trades in one document.",
  },
  {
    q: "Is there free quoting software for contractors?",
    a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month.",
  },
  {
    q: "Does contractor quoting software work for smaller firms?",
    a: "Yes. The Proper Tool is designed for contractors of all sizes, from sole traders to small and mid-size firms. The Lite plan (£29/month) gives 10 AI quotes per month with full cost library access. Larger contractors can step up to Starter (25 quotes) or Pro (50 quotes) as volume grows.",
  },
];

export default function QuotingSoftwareForContractorsPage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quoting Software for Contractors | The Proper Tool"
        description="Contractor quoting software built for UK residential construction. Generate professional, itemised quotes from drawings, photos or a description in under 10 minutes. From £29/mo, 14-day free trial."
        canonical="/quoting-software-for-contractors"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quoting Software for Contractors</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quoting Software for Contractors
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Generate accurate, professional construction quotes from drawings, photos or a job description in under 10 minutes. Built for UK residential contractors.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/comparisons">Compare alternatives</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job description to client-ready contractor quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything a contractor needs to quote faster</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Built around the UK construction market, not adapted from a generic SaaS platform built for other industries.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full contractor quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">462</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price, less than one hour of a contractor's time</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing firms", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume contractors", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/construction-estimating-software", label: "Construction Estimating Software" },
                { href: "/blog/how-to-price-building-jobs", label: "How to Price Building Jobs: A Practical Guide" },
                { href: "/best-quoting-software", label: "Best Quoting Software for Builders: 2025 Comparison" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join contractors using The Proper Tool to generate professional quotes in minutes, not hours.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
